import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { AppShell } from "./components/AppShell";
import { Login } from "./pages/Login";
import { Signup } from "./pages/Signup";
import { Overview } from "./pages/Overview";
import { Fleet } from "./pages/Fleet";
import { Pilots } from "./pages/Pilots";
import { Missions } from "./pages/Missions";
import { MissionDetail } from "./pages/MissionDetail";
import { Sessions } from "./pages/Sessions";
import { Settings } from "./pages/Settings";
import { Odr } from "./pages/Odr";

function Splash() {
  return (
    <div className="min-h-full grid place-items-center">
      <p className="text-[0.95rem] text-ink-soft">Loading…</p>
    </div>
  );
}

/** Gate that waits for the session check before deciding where to send anyone. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  if (auth.status === "loading") return <Splash />;
  if (auth.status === "anonymous") return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Keeps a signed-in user off the login and signup screens. */
function RequireAnon({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  if (auth.status === "loading") return <Splash />;
  if (auth.status === "authenticated") return <Navigate to="/app" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<RequireAnon><Login /></RequireAnon>} />
          <Route path="/signup" element={<RequireAnon><Signup /></RequireAnon>} />

          {/*
            The dashboard lives under /app so the marketing site can take "/"
            later without moving every authenticated route.
          */}
          <Route
            path="/app"
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<Overview />} />
            <Route path="fleet" element={<Fleet />} />
            <Route path="pilots" element={<Pilots />} />
            <Route path="missions" element={<Missions />} />
            <Route path="missions/:id" element={<MissionDetail />} />
            <Route path="sessions" element={<Sessions />} />
            <Route path="settings" element={<Settings />} />
            <Route path="settings/odr" element={<Odr />} />
          </Route>

          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
